import { Send, PhoneCall, Search, Building, KeyRound, Plus } from "lucide-react";

export const JOURNEY_STEPS = [
  { title: "Refer", icon: Send, text: "Share their basic details and move timeline." },
  { title: "Connect", icon: PhoneCall, text: "Bryan reaches out to learn their budget, areas and must-haves." },
  { title: "Search", icon: Search, text: "We shortlist apartments and current move-in specials that fit." },
  { title: "Tour & Apply", icon: Building, text: "They tour their favorites and we guide the application." },
  { title: "Move In", icon: KeyRound, text: "Lease signed — we stay in touch through move-in day." },
];

export const JourneySteps = ({ compact = false, idPrefix = "journey" }) => (
  <ol className={`grid gap-3 ${compact ? "grid-cols-1 sm:grid-cols-5" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-5"}`} data-testid={`${idPrefix}-steps`}>
    {JOURNEY_STEPS.map((s, i) => (
      <li key={s.title} className="flex sm:flex-col items-start gap-3 sm:gap-2" data-testid={`${idPrefix}-step-${i + 1}`}>
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#0A2342] text-[#C8A96B] flex items-center justify-center">
          <s.icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-sm font-bold text-[#0A2342]">
            <span className="text-[#C8A96B] mr-1">{i + 1}.</span>{s.title}
          </div>
          {!compact && <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{s.text}</p>}
        </div>
      </li>
    ))}
  </ol>
);

export const PortalHowItWorks = ({ persona }) => (
  <section className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8" data-testid="portal-how-it-works">
    <p className="text-[11px] uppercase tracking-widest text-[#C8A96B] font-bold">How it works</p>
    <h2 className="mt-1 text-lg md:text-xl font-bold text-[#0A2342] font-['Playfair_Display']">
      How Your {persona ? persona.label.charAt(0).toUpperCase() + persona.label.slice(1) : "Employee"} Referral Works
    </h2>
    <div className="mt-6"><JourneySteps /></div>
  </section>
);

const EMPTY_COPY = {
  corporate: "Have an employee moving to Houston? Send us their information and we'll take it from there.",
  professional: "Have a client moving to Houston? Send us their information and we'll take it from there.",
  individual: "Know someone moving to Houston? Send us their information and we'll take it from there.",
};

export const PortalEmptyState = ({ onRefer, persona }) => (
  <div className="p-6 md:p-10 text-center" data-testid="portal-no-referrals">
    <h3 className="text-xl md:text-2xl font-bold text-[#0A2342] font-['Playfair_Display']">Ready to make your first referral?</h3>
    <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto" data-testid="portal-empty-copy">
      {EMPTY_COPY[persona?.type] || EMPTY_COPY.corporate}
    </p>
    <p className="mt-6 text-sm font-semibold text-[#0A2342]" data-testid="portal-empty-flow">Refer → We Connect → We Help Them Move</p>
    <button onClick={onRefer} data-testid="portal-empty-refer-btn"
      className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#0A2342] hover:bg-[#1F2A44] text-white font-bold transition-colors">
      <Plus className="w-5 h-5" /> {persona?.cta || "Refer an Employee"}
    </button>
  </div>
);

const GROWTH_Q = { corporate: "Know another employee moving to Houston?", professional: "Know another client moving to Houston?", individual: "Know someone else moving to Houston?" };

export const PortalGrowthPrompt = ({ persona, onRefer }) => (
  <section className="rounded-2xl border border-[#C8A96B]/50 bg-[#C8A96B]/10 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="portal-growth-prompt">
    <div>
      <h3 className="text-lg font-bold text-[#0A2342] font-['Playfair_Display']">{GROWTH_Q[persona?.type] || GROWTH_Q.individual}</h3>
      <p className="text-sm text-slate-600 mt-1">Send them our way. We'll take it from there and keep you updated along the way.</p>
    </div>
    <button onClick={onRefer} data-testid="portal-growth-refer-btn"
      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#C8A96B] hover:bg-[#B5915A] text-[#1F2A44] font-bold shrink-0">
      <Plus className="w-5 h-5" /> {persona?.cta || "Refer Someone"}
    </button>
  </section>
);

export const PortalMoveInThanks = ({ names, persona, onRefer }) => (
  <section className="rounded-2xl bg-[#0A2342] text-white p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="portal-movein-thanks">
    <div className="flex items-start gap-3">
      <KeyRound className="w-6 h-6 text-[#C8A96B] mt-0.5 shrink-0" />
      <div>
        <h3 className="text-lg font-bold font-['Playfair_Display']">{names.length === 1 ? `${names[0]} made their move!` : "They made their move!"}</h3>
        <p className="text-sm text-white/80 mt-1">Thanks for trusting Urban Nest Quest with the introduction. Know someone else moving to Houston?</p>
      </div>
    </div>
    <button onClick={onRefer} data-testid="portal-movein-refer-btn"
      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#C8A96B] hover:bg-[#B5915A] text-[#1F2A44] font-bold shrink-0">
      <Plus className="w-5 h-5" /> {persona?.cta_again || "Refer Someone Else"}
    </button>
  </section>
);
